import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../lib/api'

type ReportType = 'payments' | 'rentals' | 'managers' | 'points' | 'cashouts' | 'service'

const REPORT_TYPES: { value: ReportType; label: string; description: string }[] = [
  { value: 'payments', label: '💳 Движение средств', description: 'Все платежи за период' },
  { value: 'rentals', label: '🚲 Аренды', description: 'Аренды и бронирования за период' },
  { value: 'managers', label: '👤 Менеджеры', description: 'Сводка по менеджерам' },
  { value: 'points', label: '📍 Точки', description: 'Сводка по точкам' },
  { value: 'cashouts', label: '💰 Инкассации', description: 'Все инкассации за период (+ пополнения)' },
  { value: 'service', label: '🔧 Сервис', description: 'Часы в сервисе по моделям' },
]

function toCSV(rows: Record<string, unknown>[]): string {
  if (!rows.length) return ''
  const headers = Object.keys(rows[0]!)
  return [
    headers.join(','),
    ...rows.map((row) =>
      headers.map((h) => {
        const val = row[h]; const str = val == null ? '' : String(val)
        return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str
      }).join(',')
    ),
  ].join('\n')
}

function downloadCsv(rows: Record<string, unknown>[], filename: string) {
  const csv = toCSV(rows)
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

function getDefaultDates() {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 30)
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  }
}

// Columns that get dropdown filters, per report type
const FILTERABLE_COLUMNS: Partial<Record<ReportType, string[]>> = {
  payments: ['Точка', 'Менеджер', 'Вид операции', 'Код инвентаря', 'Модель инвентаря'],
  rentals: ['Точка', 'Менеджер', 'Статус', 'Код инвентаря', 'Модель инвентаря', 'Кол-во велосипедов'],
  cashouts: ['Точка', 'Менеджер'],
  service: ['Модель'],
}

function normalizeFilterValue(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return String(v).trim().normalize('NFC')
}

// Columns whose cell can hold several models joined by "; " (multi-bike rentals).
// They filter by token membership so «818» matches both a solo «818» rental and a
// combined «818; 702» one.
// Columns whose cell can hold several values joined by "; " (multi-bike rentals):
// model AND inventory code. They filter by token membership (#14).
const MODEL_COLUMNS = new Set(['Модель инвентаря', 'Модель', 'Код инвентаря'])

// Wide free-text columns that should wrap instead of forcing the table wide (#14).
const WRAP_COLUMNS = new Set(['Модель инвентаря', 'Модель', 'Примечание', 'Заметка', 'Комментарий'])

function splitModels(v: unknown): string[] {
  return String(v ?? '')
    .split(';')
    .map((s) => s.trim().normalize('NFC'))
    .filter(Boolean)
}

function rowMatchesColumnFilter(row: Record<string, unknown>, col: string, filterVal: string): boolean {
  const fv = filterVal.trim().normalize('NFC')
  if (!fv) return true
  if (MODEL_COLUMNS.has(col)) return splitModels(row[col]).includes(fv)
  return normalizeFilterValue(row[col]) === fv
}

export default function ReportsPage() {
  const [searchParams] = useSearchParams()
  const defaults = getDefaultDates()

  const urlType = searchParams.get('type') as ReportType | null
  const urlFrom = searchParams.get('from')
  const urlTo = searchParams.get('to')

  const [reportType, setReportType] = useState<ReportType>(
    urlType && REPORT_TYPES.some((r) => r.value === urlType) ? urlType : 'payments'
  )
  const [from, setFrom] = useState(urlFrom ?? defaults.from)
  const [to, setTo] = useState(urlTo ?? defaults.to)
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({})

  const generate = useMutation({
    mutationFn: () =>
      api.get('/reports', { params: { type: reportType, from, to } }).then((r) => r.data),
    onSuccess: (data: { data: Record<string, unknown>[] }) => {
      setRows(data.data)
      setColumnFilters({})
    },
  })

  const filterableColumns = FILTERABLE_COLUMNS[reportType] ?? []

  // Get unique values for a column
  const getUniqueValues = (col: string): string[] => {
    if (!rows) return []
    const vals = new Set<string>()
    for (const row of rows) {
      if (MODEL_COLUMNS.has(col)) {
        for (const m of splitModels(row[col])) vals.add(m)
      } else {
        const v = String(row[col] ?? '')
        if (v) vals.add(v)
      }
    }
    return Array.from(vals).sort()
  }

  // Apply filters to rows (normalize strings so Chrome / API quirks don’t break Точка / Менеджер)
  const filteredRows = rows
    ? rows.filter((row) =>
        Object.entries(columnFilters).every(([col, val]) => rowMatchesColumnFilter(row, col, val))
      )
    : null

  // Auto-run report when navigated from dashboard with URL params
  useEffect(() => {
    if (urlType && urlFrom && urlTo) {
      generate.mutate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const headers = rows && rows.length > 0 ? Object.keys(rows[0]!) : []
  const selectedType = REPORT_TYPES.find((t) => t.value === reportType)!

  const totalsRow: Record<string, unknown> | null = filteredRows && filteredRows.length > 0
    ? (() => {
        const totals: Record<string, unknown> = {}
        for (const h of headers) {
          const allNumeric = filteredRows.every((r) => typeof r[h] === 'number')
          if (allNumeric) {
            const sum = filteredRows.reduce((s, r) => s + (r[h] as number), 0)
            totals[h] = h.startsWith('Ср.') ? Math.round(sum / filteredRows.length) : sum
          } else {
            totals[h] = ''
          }
        }
        totals[headers[0]!] = 'Итого'
        return totals
      })()
    : null

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-6">Отчёты</h2>

      {/* Report type selector */}
      <div className="space-y-2 mb-5">
        {REPORT_TYPES.map((t) => (
          <button
            key={t.value}
            onClick={() => { setReportType(t.value); setRows(null) }}
            className={`w-full text-left px-4 py-3 rounded-2xl border transition-colors ${reportType === t.value ? 'bg-brand-600 border-brand-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-brand-300'}`}
          >
            <p className="font-medium text-sm">{t.label}</p>
            <p className={`text-xs mt-0.5 ${reportType === t.value ? 'text-brand-100' : 'text-gray-400'}`}>{t.description}</p>
          </button>
        ))}
      </div>

      {/* Date range */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4">
        <p className="text-sm font-semibold text-gray-700 mb-3">Период</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">С</label>
            <input
              type="date"
              value={from}
              onChange={(e) => { setFrom(e.target.value); setRows(null) }}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">По</label>
            <input
              type="date"
              value={to}
              onChange={(e) => { setTo(e.target.value); setRows(null) }}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Generate button */}
      <button
        onClick={() => generate.mutate()}
        disabled={generate.isPending}
        className="w-full bg-brand-600 text-white py-4 rounded-2xl font-bold text-base disabled:opacity-60 mb-4"
      >
        {generate.isPending ? 'Формирование...' : `Сформировать — ${selectedType.label}`}
      </button>

      {generate.isError && (
        <p className="text-red-500 text-sm text-center mb-4">
          {(generate.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка'}
        </p>
      )}

      {/* Results */}
      {rows !== null && filteredRows !== null && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700">
              Результат: {filteredRows.length} строк
              {filteredRows.length !== rows.length && <span className="text-gray-400 font-normal"> (всего {rows.length})</span>}
            </p>
            {rows.length > 0 && (
              <button
                onClick={() => {
                  // CSV = только строки данных (с учётом фильтра), БЕЗ строки «Итого».
                  // Иначе при суммировании столбца в Excel итог удваивается (#отчёты).
                  downloadCsv(filteredRows ?? rows, `report-${reportType}-${from}-${to}.csv`)
                }}
                className="text-sm border border-brand-300 text-brand-600 px-4 py-1.5 rounded-xl hover:bg-brand-50"
              >
                CSV ↓
              </button>
            )}
          </div>

          {rows.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">За выбранный период данных нет</p>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-gray-200">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {headers.map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                  <tr className="bg-gray-50/80 border-b border-gray-200">
                    {headers.map((h) => {
                      const isFilterable = filterableColumns.includes(h)
                      const uniqueVals = isFilterable && rows ? getUniqueValues(h) : []
                      const showSelect = Boolean(isFilterable && rows && rows.length > 0 && uniqueVals.length >= 1)
                      return (
                        <th key={`filter-${h}`} className="px-3 py-2 align-top font-normal">
                          {showSelect ? (
                            <select
                              aria-label={`Фильтр: ${h}`}
                              value={columnFilters[h] ?? ''}
                              onChange={(e) => setColumnFilters((prev) => ({ ...prev, [h]: e.target.value }))}
                              className="w-full min-w-[6rem] max-w-[11rem] font-normal text-gray-700 border border-gray-300 rounded-lg px-1 py-1 text-xs bg-white"
                            >
                              <option value="">Все</option>
                              {uniqueVals.map((v, i) => (
                                <option key={`${h}-${i}-${v}`} value={v}>
                                  {v}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="inline-block min-h-[1.75rem]" />
                          )}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredRows.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      {headers.map((h) => (
                        <td
                          key={h}
                          className={`px-3 py-2 text-gray-700 ${WRAP_COLUMNS.has(h) ? 'align-top' : 'whitespace-nowrap'}`}
                        >
                          {WRAP_COLUMNS.has(h) ? (
                            <div className="max-w-[16rem] whitespace-normal break-words">{String(row[h] ?? '—')}</div>
                          ) : (
                            String(row[h] ?? '—')
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {totalsRow && (
                    <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                      {headers.map((h) => (
                        <td key={h} className="px-3 py-2 text-gray-900 whitespace-nowrap">
                          {totalsRow[h] === '' ? '—' : String(totalsRow[h])}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
